import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Button,
  TextField,
  Snackbar,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  Box,
  IconButton,
  Switch,
  ImageListItem,
  ImageListItemBar,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import { AppDispatch, RootState } from "../redux/store";
import {
  listSupportCenters,
  updateSupportCenter,
} from "../redux/slices/supportSlice";
import useDetectSession from "../hooks/useDetectSession.hook";
import {
  inputStyle,
  pageHeading,
  primaryBtnStyle,
  primaryBtnStyleOutline,
} from "../styles/styling";
import AppContainer from "../components/AppContainer";
import { PermissionsByDocKey } from "../interfaces/permissions-interface";
import { transformArrToObj } from "../utils/transform-permissions-obj";
import LoadingOverlay from "../components/LoadingOverlay";

interface SupportCenter {
  email?: string;
  landline?: string;
  viber?: string;
  hotline?: string | string[];
  isHidden: boolean;
  logo?: string;
}

const SupportPage: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();

  useDetectSession();

  const { supportCenters, status, error } = useSelector(
    (state: RootState) => state.support
  );
  const currentUser = useSelector((state: RootState) => state.auth.currentUser);

  const [currentUserPermissions, setCurrentUserPermissions] =
    useState<PermissionsByDocKey>();

  const [editedCenterKey, setEditedCenterKey] = useState<string | null>(null);
  const [editedSupportCenter, setEditedSupportCenter] =
    useState<SupportCenter | null>(null);
  const [openSnackbar, setOpenSnackbar] = useState<boolean>(false);
  const [snackbarMessage, setSnackbarMessage] = useState<string>("");
  const [snackbarSeverity, setSnackbarSeverity] = useState<"success" | "error">(
    "success"
  );

  useEffect(() => {
    dispatch(listSupportCenters());
  }, [dispatch]);

  useEffect(() => {
    if (currentUser && currentUser.userPermissions) {
      setCurrentUserPermissions(transformArrToObj(currentUser.userPermissions));
    }
  }, [currentUser]);

  const handleEditClick = (key: string, center?: SupportCenter | null) => {
    setEditedCenterKey(key);
    setEditedSupportCenter(center ?? null);
  };

  const handleHiddenToggle = (event: React.ChangeEvent<HTMLInputElement>) => {
    setEditedSupportCenter({
      ...editedSupportCenter!,
      isHidden: event.target.checked,
    });
  };

  const handleSaveClick = () => {
    if (editedCenterKey && editedSupportCenter) {
      const obj = {
        documentName: editedCenterKey,
        updateFields: editedSupportCenter,
        updatedBy: currentUser?.fullname ?? "",
      };
      dispatch(updateSupportCenter(obj))
        .unwrap()
        .then(() => {
          setSnackbarMessage("Support center updated successfully!");
          setSnackbarSeverity("success");
          setOpenSnackbar(true);
          setEditedCenterKey(null);
          setEditedSupportCenter(null);
          dispatch(listSupportCenters());
        })
        .catch((err: any) => {
          setSnackbarMessage(`Failed to update support center: ${err}`);
          setSnackbarSeverity("error");
          setOpenSnackbar(true);
        });
    }
  };

  if (status === "failed") {
    return (
      <AppContainer>
        <Typography variant="h6" color="error">
          Error: {error}
        </Typography>
      </AppContainer>
    );
  }

  return (
    <AppContainer>
      <Box display="flex" alignItems="center" marginBottom={2}>
        <Typography variant="h4" sx={pageHeading}>
          Support Centers
        </Typography>
      </Box>

      <LoadingOverlay loading={status === "loading"} />
      <TableContainer
        component={Paper}
        sx={{
          overflowX: "initial",
          "& th": {
            backgroundColor: "#E1E1E1",
          },
          width: "100%",
        }}
      >
        <Table style={{ width: "100%" }}>
          <TableHead>
            <TableRow>
              <TableCell>Center</TableCell>
              <TableCell>Email</TableCell>
              <TableCell>Landline</TableCell>
              <TableCell>Viber</TableCell>
              <TableCell>Mobile Number</TableCell>
              <TableCell>Hidden</TableCell>
              {currentUserPermissions?.contactSupport?.permissions?.edit && (
                <TableCell>Actions</TableCell>
              )}
            </TableRow>
          </TableHead>
          <TableBody>
            {supportCenters &&
              Object.entries(supportCenters).map(([key, center]) => (
                <TableRow key={key} style={{ height: 300 }}>
                  <TableCell
                    style={{
                      width: "30%",
                    }}
                  >
                    <ImageListItem
                      key={key}
                      style={{ width: 250, height: 150, marginTop: 20 }}
                    >
                      <img
                        srcSet={center.logo}
                        src={center.logo}
                        alt={key}
                        loading="lazy"
                        style={{ objectFit: "contain" }}
                      />
                      <ImageListItemBar
                        title={key}
                        position="top"
                        style={{ textAlign: "center", top: -40 }}
                      />
                    </ImageListItem>
                    {editedCenterKey === key && (
                      <TextField
                        label="Logo URL"
                        value={editedSupportCenter?.logo}
                        onChange={(e) =>
                          setEditedSupportCenter({
                            ...editedSupportCenter!,
                            logo: e.target.value,
                          })
                        }
                        fullWidth
                        required
                        sx={[inputStyle(), { marginTop: 20 }]}
                      />
                    )}
                  </TableCell>
                  <TableCell style={{ width: "11%" }}>
                    {editedCenterKey === key ? (
                      <TextField
                        value={editedSupportCenter?.email}
                        onChange={(e) =>
                          setEditedSupportCenter({
                            ...editedSupportCenter!,
                            email: e.target.value,
                          })
                        }
                        fullWidth
                        sx={inputStyle()}
                      />
                    ) : (
                      center.email
                    )}
                  </TableCell>
                  <TableCell style={{ width: "11%" }}>
                    {editedCenterKey === key ? (
                      <TextField
                        value={editedSupportCenter?.landline}
                        onChange={(e) =>
                          setEditedSupportCenter({
                            ...editedSupportCenter!,
                            landline: e.target.value,
                          })
                        }
                        fullWidth
                        sx={inputStyle()}
                      />
                    ) : (
                      center.landline
                    )}
                  </TableCell>
                  <TableCell style={{ width: "11%" }}>
                    {editedCenterKey === key ? (
                      <TextField
                        value={editedSupportCenter?.viber}
                        onChange={(e) =>
                          setEditedSupportCenter({
                            ...editedSupportCenter!,
                            viber: e.target.value,
                          })
                        }
                        fullWidth
                        sx={inputStyle()}
                      />
                    ) : (
                      center.viber
                    )}
                  </TableCell>
                  <TableCell style={{ width: "11%" }}>
                    {editedCenterKey === key ? (
                      <TextField
                        value={
                          Array.isArray(editedSupportCenter?.hotline)
                            ? editedSupportCenter?.hotline.join(", ")
                            : editedSupportCenter?.hotline
                        }
                        onChange={(e) =>
                          setEditedSupportCenter({
                            ...editedSupportCenter!,
                            hotline: e.target.value.split(", "),
                          })
                        }
                        fullWidth
                        sx={inputStyle()}
                      />
                    ) : Array.isArray(center.hotline) ? (
                      center.hotline.join(", ")
                    ) : (
                      center.hotline
                    )}
                  </TableCell>
                  <TableCell style={{ width: "6%" }}>
                    {editedCenterKey === key ? (
                      <Switch
                        checked={editedSupportCenter?.isHidden || false}
                        onChange={handleHiddenToggle}
                      />
                    ) : center.isHidden ? (
                      "Yes"
                    ) : (
                      "No"
                    )}
                  </TableCell>
                  {currentUserPermissions?.contactSupport?.permissions
                    ?.edit && (
                    <TableCell
                      style={{
                        width: "20%",
                        flexDirection: "column",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      {editedCenterKey === key ? (
                        <>
                          <Button
                            variant="contained"
                            color="primary"
                            onClick={handleSaveClick}
                            disabled={!editedSupportCenter?.logo}
                            style={{ margin: 5 }}
                            sx={primaryBtnStyle}
                          >
                            Save
                          </Button>
                          <Button
                            variant="contained"
                            color="primary"
                            onClick={() => handleEditClick("", null)}
                            style={{ margin: 5 }}
                            sx={primaryBtnStyleOutline}
                          >
                            Cancel
                          </Button>
                        </>
                      ) : (
                        <IconButton
                          onClick={() => handleEditClick(key, center)}
                        >
                          <EditIcon />
                        </IconButton>
                      )}
                    </TableCell>
                  )}
                </TableRow>
              ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Snackbar
        open={openSnackbar}
        autoHideDuration={3000}
        onClose={() => setOpenSnackbar(false)}
      >
        <Alert
          onClose={() => setOpenSnackbar(false)}
          severity={snackbarSeverity}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </AppContainer>
  );
};

export default SupportPage;
